import { Seo } from "@/lib/seo";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { NewsletterSignup } from "@/components/NewsletterSignup";

function LegalShell({ title, path, description, children }) {
  return (
    <div className="mx-auto max-w-3xl px-5 sm:px-8 py-12">
      <Seo title={`${title} | Hotelstrips`} description={description} path={path} />
      <Breadcrumbs items={[{ label: title }]} />
      <h1 className="font-heading text-4xl font-black tracking-tighter text-ink mb-2">{title}</h1>
      <p className="text-sm text-muted-foreground mb-10">Hotelstrips.com — owned and operated by Titan Haven Group LLC.</p>
      <div className="space-y-6 text-foreground/80 leading-relaxed">{children}</div>
    </div>
  );
}

function H2({ children }) { return <h2 className="font-heading text-xl font-bold text-ink pt-4">{children}</h2>; }

export function PrivacyPage() {
  return (
    <LegalShell title="Privacy Policy" path="/privacy" description="How Hotelstrips, a Titan Haven Group company, handles data and analytics.">
      <p>This Privacy Policy explains how Hotelstrips.com ("Hotelstrips", "we"), operated by Titan Haven Group LLC, handles information when you use our site.</p>
      <H2>Information we collect</H2>
      <p>We collect minimal, non-identifying information: an anonymous session identifier stored in your browser, pages viewed, and outbound affiliate link clicks. If you subscribe to our newsletter, we store the email address you provide.</p>
      <H2>Analytics & cookies</H2>
      <p>We may use privacy-conscious analytics (for example Google Analytics) configured via environment variables. These tools may set cookies to measure aggregate traffic and performance. You can control cookies through your browser settings.</p>
      <H2>Affiliate links</H2>
      <p>When you click certain outbound links, we record the click and a tracking identifier so we can understand demand. We do not sell personal information.</p>
      <H2>Contact</H2>
      <p>Questions? Reach us via the <a href="/contact" className="text-primary underline">contact page</a>.</p>
    </LegalShell>
  );
}

export function TermsPage() {
  return (
    <LegalShell title="Terms of Use" path="/terms" description="Terms governing use of Hotelstrips.com.">
      <p>By accessing Hotelstrips.com you agree to these Terms of Use. The site is operated by Titan Haven Group LLC.</p>
      <H2>Editorial content</H2>
      <p>Our stay-area recommendations, rankings and area guides are editorial content provided for planning guidance. We do not guarantee live schedules, availability, or pricing. Always confirm details with the relevant provider before booking.</p>
      <H2>Third-party booking</H2>
      <p>Bookings are completed on third-party provider websites. We are not responsible for the products, prices, or services of those providers.</p>
      <H2>Acceptable use</H2>
      <p>You agree not to misuse the site, attempt unauthorized access, or scrape content without permission.</p>
      <H2>Changes</H2>
      <p>We may update these terms from time to time. Continued use constitutes acceptance of the current terms.</p>
    </LegalShell>
  );
}

export function AffiliateDisclosurePage() {
  return (
    <LegalShell title="Affiliate Disclosure" path="/affiliate-disclosure" description="How Hotelstrips earns compensation through affiliate partners.">
      <p className="text-lg text-ink font-semibold">Hotelstrips may earn compensation when you book through certain links on this site.</p>
      <p>Hotelstrips.com, operated by Titan Haven Group LLC, participates in affiliate and partner programs (such as Travelpayouts and others). When you click an outbound "book", "find hotels", "search flights", or similar link and complete a qualifying action, we may receive a commission at no additional cost to you.</p>
      <H2>Our commitment</H2>
      <p>Our stay-area recommendations, rankings and editorial guidance are determined by Hotelstrips — not by any affiliate provider. Recommendations are not for sale. Affiliate relationships never change which areas or venues we rank highest.</p>
      <H2>Sponsored content</H2>
      <p>Any sponsored placement will be clearly labeled as "Sponsored" and visually distinguished from editorial content.</p>
    </LegalShell>
  );
}

export function AboutPage() {
  return (
    <LegalShell title="About Hotelstrips" path="/about" description="Hotelstrips is a travel discovery and planning platform, a Titan Haven Group company.">
      <p className="text-lg text-ink font-semibold">Travel for the event. Stay for the experience.</p>
      <p>Hotelstrips is a travel discovery, planning and commerce platform built around a simple question: <em>where should I stay when I travel for a game, event, concert or tournament?</em></p>
      <p>Our launch vertical, <strong>Hotelstrips GameDay</strong>, maps the best areas to stay for major venues — with the transit, airport, nightlife and family context you actually need — before you ever reach a booking provider. Hotelstrips owns the recommendation layer; affiliate partners handle the transaction.</p>
      <p>Hotelstrips.com is a standalone consumer travel property owned and operated by <strong>Titan Haven Group LLC</strong>.</p>
      <div className="pt-4"><NewsletterSignup /></div>
    </LegalShell>
  );
}

export function ContactPage() {
  return (
    <LegalShell title="Contact" path="/contact" description="Get in touch with the Hotelstrips team.">
      <p>We'd love to hear from you — partnership inquiries, corrections, or feedback on a venue guide.</p>
      <p>Email: <a href="mailto:hello@hotelstrips.com" className="text-primary underline">hello@hotelstrips.com</a></p>
      <p>Operated by Titan Haven Group LLC.</p>
      <div className="pt-4">
        <div className="overline text-primary mb-3">Join the newsletter</div>
        <NewsletterSignup />
      </div>
    </LegalShell>
  );
}
